import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  contentChild,
  DestroyRef,
  effect,
  EffectCleanupRegisterFn,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';

import { ActionService } from '../../services/action/action.service';
import { Maybe, ValueOf } from '../../types/base';
import { MenuItemType } from '../../types/menu-item.type';
import { CollapsiblePanelComponent } from '../_legacy/collapsible/collapsible-panel.component';
import { CloseOnClickOutsideDirective } from './../../directives/close-on-click-outside/close-on-click-outside.directive';
import { MenuItemComponent } from './menu-item/menu-item.component';
import { MenuTriggerStateDirective } from './menu-trigger-state';

export type TopPosition = 'top' | 'bottom';
export type LeftPosition = 'left' | 'right' | 'center';
export type MenuPosition = `${TopPosition}-${LeftPosition}`;

export const menuModes = {
  /** default value */
  DROPDOWN: 'dropdown',
  COLLAPSE: 'collapse',
} as const;
export type MenuMode = ValueOf<typeof menuModes>;

@Component({
  selector: 'evc-menu',
  imports: [
    MenuItemComponent,
    CloseOnClickOutsideDirective,
    CollapsiblePanelComponent,
  ],
  templateUrl: './menu.html',
  styleUrl: './menu.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [CloseOnClickOutsideDirective],
})
export class MenuComponent implements AfterViewInit {
  readonly items = input<MenuItemType[]>([]);
  readonly title = input<Maybe<string>>();
  readonly position = input<MenuPosition>('bottom-center');
  readonly mode = input<MenuMode>(menuModes.DROPDOWN);

  readonly triggerWrapper = viewChild<ElementRef>('trigger');
  triggerState = contentChild(MenuTriggerStateDirective);

  readonly #closeOnClickOutside = inject(CloseOnClickOutsideDirective);
  readonly #actionService = inject(ActionService);
  readonly #destroyRef = inject(DestroyRef);

  readonly MODES = menuModes;

  readonly isOpen = input<Maybe<boolean>>(undefined);
  /** Emits when the menu transitions to closed (item selected or dismissed). */
  readonly closed = output();
  readonly isOpenInternal = signal(false);

  /* keep local copy to update current selected */
  internalItems = signal<MenuItemType[]>([]);
  #syncItems = effect(() => {
    this.internalItems.set(this.items());
  });

  #registerAction = effect((onCleanup: EffectCleanupRegisterFn) => {
    const triggerWrapper = this.triggerWrapper();
    untracked(() => this.#registerTriggerAction(onCleanup, triggerWrapper?.nativeElement));
  });

  constructor() {
    effect(() => {
      const isOpen = this.isOpen();
      if (isOpen === undefined) return;
      this.toggle(isOpen);
    });
  }

  ngAfterViewInit(): void {
    this.#registerClickOutsideHandler();
  }

  toggle(state?: boolean): void {
    const wasOpen = this.isOpenInternal();
    this.isOpenInternal.update((open) => {
      const isOpen = state ?? !open;
      this.triggerState()?.setSelected(isOpen);

      return isOpen;
    });

    if (wasOpen && !this.isOpenInternal()) {
      this.closed.emit();
    }
  }

  handleCollapsibleItemClick(item: MenuItemType, collapsibleItemClicked: MenuItemType): void {
    this.#actionService.handleAction(collapsibleItemClicked, this.#destroyRef);

    this.internalItems.update((items) =>
      items.map((currentItem) =>
        currentItem.key === item.key
          ? {
              ...currentItem,
              collapsible: currentItem.collapsible?.map((currentCollapsibleItem) => ({
                ...currentCollapsibleItem,
                selected: currentCollapsibleItem.key === collapsibleItemClicked.key,
              })),
            }
          : currentItem,
      ),
    );

    this.toggle(false);
  }

  #registerTriggerAction(onCleanup: EffectCleanupRegisterFn, triggerWrapper?: HTMLElement): void {
    if (!triggerWrapper) return;

    const handler = this.toggle.bind(this, undefined);
    triggerWrapper.addEventListener('click', handler);
    onCleanup(() => {
      triggerWrapper.removeEventListener('click', handler);
    });
  }

  #registerClickOutsideHandler(): void {
    const subscription = this.#closeOnClickOutside.clickedOutside.subscribe(() => {
      if (!this.isOpenInternal()) return;
      if (this.isOpen() !== undefined) {
        this.closed.emit();
        return;
      }
      this.toggle(false);
    });

    this.#destroyRef.onDestroy(() => {
      subscription.unsubscribe();
    });
  }
}
